import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import * as tracking from 'dibs-tracking';
import { getAbTestTrackingData } from 'dibs-buyer-layout/exports/clientABTestHelpers';
import serverVars from 'server-vars';
import { CUSTOMER_TYPE_TRADE } from '../constants/userConstants';
import { pageTypeConstants as PAGE_TYPES, pageTypeConstants } from '../constants/pageTypeConstants';
import { getPageviewData } from '../utils/tracking/searchBrowse/getPageviewData';

import { SharedUserAndRelayVarLoader } from '../shared/SharedUserAndRelayVarLoader';

import { updateRelayVariables } from '../actions/sharedRelayVariableActions';
import { toggleRequestInFlight, updateUriRef } from '../actions/filterActions';

import { getNewPaginationUriRef, addCdnQueryParams } from '../utils/uriUtils';

import { FindingRouter } from '../router/FindingRouter';
import { isPersonalizedRerankAbTestVariant } from 'dibs-buyer-layout/src/utils/abTest/personalizedRerankAbTestHelpers';
import { personalizedRerankTracking } from '../finding/SbSharedTrackingFunctions/SbSharedTrackingFunctions';

import { handleSbUserCountryCode } from '../finding/helpers/handleSbUserCountryCode';

class BuySharedRefetchContainerComponent extends Component {
    constructor() {
        super();

        this.handleUserRefetch = this.handleUserRefetch.bind(this);
        this.handleBuyPageRefetch = this.handleBuyPageRefetch.bind(this);
        this.refetchCallback = this.refetchCallback.bind(this);
        this.handlePageChange = this.handlePageChange.bind(this);
    }

    componentDidMount() {
        const { buyPage } = this.props;
        const { pageType, totalResults, appliedFilters, displayUriRef } = buyPage;
        this.buyPageName = appliedFilters.find(
            f => f.name === PAGE_TYPES.BUY
        )?.values?.[0]?.urlLabel;
        this.router = new FindingRouter({ routes: [{ path: '/buy/:pageName?' }] });
        this.props.updateUriRef({ uriRef: displayUriRef });
        tracking.trackPageview({
            additional: {
                ...getPageviewData({
                    pageType,
                    totalResults,
                    appliedFilters,
                }),
            },
        });
        const trackingData = getAbTestTrackingData(document.cookie, 'NEW_TAB_TEST');
        tracking.trackAbTestEvent(trackingData, 'results_pageview');
        personalizedRerankTracking({
            pageType: pageTypeConstants.BUY,
            sortValue: buyPage?.sort?.currentOption?.urlLabel,
        });
    }

    componentDidUpdate(prevProps) {
        const { generatedUriRef, page: newPage } = this.props;
        const oldUriRef = addCdnQueryParams(prevProps.generatedUriRef);
        const newUriRef = addCdnQueryParams(generatedUriRef);
        const uriRefChanged = oldUriRef !== '' && newUriRef !== oldUriRef;
        const pageChanged = newPage !== prevProps.page;
        let uriRef = generatedUriRef;

        if (pageChanged) {
            uriRef = getNewPaginationUriRef(generatedUriRef, newPage);
            this.handlePageChange(newPage);
        }

        if (uriRefChanged) {
            const variables = { uriRef, page: newPage };
            this.props.toggleRequestInFlight(true);
            this.handleBuyPageRefetch(
                { ...variables, fetchBuy: true, skipUser: true },
                null,
                this.refetchCallback
            );
            this.props.updateRelayVariables(variables);
        }
    }

    refetchCallback() {
        this.props.toggleRequestInFlight(false);
        this.props.updateUriRef({ uriRef: this.props?.buyPage?.displayUriRef });
    }

    handlePageChange(page) {
        if (this.buyPageName) {
            this.router.navigate(
                getNewPaginationUriRef(`/${PAGE_TYPES.BUY}/${this.buyPageName}/`, page),
                { replace: true }
            );
        }
    }

    handleUserRefetch(variables, renderVariables, callback) {
        /**
         * Need to refetch sb items for trade users, the price depends on the `isTrade` variable
         * being set.
         */
        const { buyPage, relayVariables } = this.props;
        const { displayUriRef } = buyPage || {};
        const { followSearchPages, priceBookName } = relayVariables || {};
        const fetchBuy =
            (variables.isTrade && serverVars.get('userType') !== CUSTOMER_TYPE_TRADE) ||
            isPersonalizedRerankAbTestVariant();
        this.handleBuyPageRefetch(
            {
                ...variables,
                fetchBuy,
                skipUser: false,
                followSearchPages: displayUriRef ? [displayUriRef] : followSearchPages,
                priceBookName,

                userCountryCode: handleSbUserCountryCode({
                    userCountryCode: variables?.userCountryCode,
                    fetchSb: variables?.fetchSb,
                }),
            },
            renderVariables,
            callback
        );
    }

    handleBuyPageRefetch(variables, renderVariables, callback) {
        this.props.relay.refetch(variables, renderVariables, callback);
    }

    render() {
        const { relay } = this.props;

        return (
            <SharedUserAndRelayVarLoader
                onUserRefetch={this.handleUserRefetch}
                environment={relay.environment}
            />
        );
    }
}

function mapStateToProps(state) {
    const { filters, relayVariables } = state;
    const { generatedUriRef, page } = filters;

    return {
        page,
        generatedUriRef,
        relayVariables: relayVariables.variables,
    };
}

BuySharedRefetchContainerComponent.propTypes = {
    relay: PropTypes.object.isRequired,
    generatedUriRef: PropTypes.string,
    toggleRequestInFlight: PropTypes.func.isRequired,
    updateUriRef: PropTypes.func.isRequired,
    page: PropTypes.number.isRequired,
    updateRelayVariables: PropTypes.func.isRequired,
    buyPage: PropTypes.object,
    relayVariables: PropTypes.shape({
        followSearchPages: PropTypes.arrayOf(PropTypes.string),
        userCountryCode: PropTypes.string,
        priceBookName: PropTypes.string,
    }).isRequired,
};

export const BuySharedRefetchContainer = connect(mapStateToProps, {
    toggleRequestInFlight,
    updateUriRef,
    updateRelayVariables,
})(BuySharedRefetchContainerComponent);
